/* eslint-disable @typescript-eslint/no-explicit-any */
import { DOCUMENT, inject, Injectable, OnDestroy } from '@angular/core';
import { MonoTypeOperatorFunction, Observable, of, Subject } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { TTL } from '../../environments/plugins';
import { DataProvider } from '../providers/data.provider';
import { StorageProvider } from './storage.provider';

@Injectable({ providedIn: 'root' })
export class UtilityService implements OnDestroy {
	readonly destroy = new Subject();

	private dataProvider = inject(DataProvider);
	private storage = inject(StorageProvider);
	private document: Document = inject(DOCUMENT);

	setCaching(name: string): MonoTypeOperatorFunction<any> {
		return tap((data) => {
			if (data && data.isSuccess) {
				const expiresIn = new Date();
				expiresIn.setSeconds(expiresIn.getSeconds() + TTL);
				this.storage.setItem(name, JSON.stringify({ expiresIn, body: data }));
			}
		});
	}

	getFooterDisclaimer(): Observable<any> {
		const componentName = 'footer_template';
		const componentData = this.storage.getItem(componentName);

		if (!componentData) {
			return this.getFooterDisclaimerAPI(componentName);
		}

		const page = JSON.parse(componentData);

		if (!page) {
			return this.getFooterDisclaimerAPI(componentName);
		}

		const expiresInDate = new Date(page.expiresIn);

		if (expiresInDate && expiresInDate.getTime() < new Date().getTime()) {
			return this.getFooterDisclaimerAPI(componentName);
		}

		return of(page.body.text);
	}

	getFooterDisclaimerAPI(name: string): Observable<any> {
		return this.dataProvider.getData('utils/getfooterbytemplatenameandwebsiteid/3/' + environment.id).pipe(
			this.setCaching(name),
			takeUntil(this.destroy),
			map((data: any) => (data && data.isSuccess ? data.text : ''))
		);
	}

	getWebPage(websiteId: number, pageName: string): Observable<any> {
		const storedPage = this.storage.getItem(pageName);

		if (storedPage === null) {
			return this.getWebPageAPI(websiteId, pageName);
		}

		const page = JSON.parse(storedPage);

		const expiresInDate = new Date(page.expiresIn);

		if (expiresInDate && expiresInDate.getTime() < new Date().getTime()) {
			return this.getWebPageAPI(websiteId, pageName);
		}

		return of(page.body);
	}

	getWebPageAPI(websiteId: number, pageName: string): Observable<any> {
		return this.dataProvider.getData('items/GetWebPageContentByWebsiteIdAndWebPageName/' + websiteId + '/' + pageName).pipe(
			this.setCaching(pageName),
			map((data) => (data && data.isSuccess ? data : null))
		);
	}

	getRegulatoryDisclosures(): Observable<any> {
		const componentName = 'regulatory_websites';

		const componentData = this.storage.getItem(componentName);

		if (!componentData) {
			return this.getRegulatoryDisclosuresAPI(componentName);
		}

		const page = JSON.parse(componentData);

		if (!page) {
			return this.getRegulatoryDisclosuresAPI(componentName);
		}

		const expiresInDate = new Date(page.expiresIn);

		if (expiresInDate && expiresInDate.getTime() < new Date().getTime()) {
			return this.getRegulatoryDisclosuresAPI(componentName);
		}

		return of(page.body);
	}

	getRegulatoryDisclosuresAPI(name: string): Observable<any> {
		return this.dataProvider.getData('utils/getregulatorydisclosuresforallwebsites').pipe(
			this.setCaching(name),
			takeUntil(this.destroy),
			map((data) => (data && data.isSuccess ? data : ''))
		);
	}

	toggleNav(toggle: boolean): void {
		const menuBarToggle = this.document.getElementById('menu-bar-toggle');
		const menuBar = this.document.getElementsByTagName('app-menu-bar-component');

		if (!menuBarToggle || !menuBar) {
			return;
		}

		if (toggle) {
			// IF IT OPENS
			menuBarToggle.classList.add('active');
			menuBar[0].classList.add('active');
		}
		// IF IT CLOSES
		else {
			menuBar[0].classList.replace('active', 'closing'); // LET THE ANIMATION FLOW
			setTimeout(() => {
				menuBar[0].removeAttribute('class');
			}, 2800);
		}
	}

	ngOnDestroy(): void {
		this.destroy.next('');
		this.destroy.complete();
		this.destroy.unsubscribe();
	}
}
